import type { ClampQcInspectionRow } from "./clampQcMockData";

export type ClampQcSessionRescanConfirmation = {
  trimmedSerial: string;
  serialKey: number;
};

export function clampQcFindSessionEntryBySerialKey<T extends Pick<ClampQcInspectionRow, "serial">>(
  sessionEntries: readonly T[],
  serialKey: number,
): T | undefined {
  return sessionEntries.find((entry) => entry.serial === serialKey);
}

/** True when modal submit may proceed (new serial, or rescan armed for this serial key). */
export function clampQcSessionRescanAllowsModalSubmit(
  sessionEntries: readonly Pick<ClampQcInspectionRow, "serial">[],
  serialKey: number,
  armedSerialKey: number | null,
): boolean {
  const duplicateInSession = sessionEntries.some((entry) => entry.serial === serialKey);
  return !duplicateInSession || armedSerialKey === serialKey;
}

export function clampQcFormatMeasurementPrefill(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "";
  }
  return String(value);
}

export function clampQcFailReasonLabelsFromSessionRow(failReason: string | null): string[] {
  if (failReason == null || failReason.trim() === "") {
    return [];
  }
  return failReason
    .split(";")
    .map((label) => label.trim())
    .filter((label) => label !== "");
}

/** Printed carton face code from compact inventory box hex (first eight characters). */
export function clampQcInventoryBoxShortCodeFromHex(hex: string): string {
  const compact = hex.replace(/-/g, "").trim().toLowerCase();
  if (compact.length < 8) {
    return hex.trim();
  }
  return `${compact.slice(0, 4).toUpperCase()}-${compact.slice(4, 8).toUpperCase()}`;
}

export type ClampQcSessionRescanCartonSummary = {
  heading: string;
  detail: string;
};

export function clampQcSessionRescanCartonSummary(input: {
  result: string;
  cartonInventoryBoxHex?: string | null;
  openCartonHex?: string | null;
}): ClampQcSessionRescanCartonSummary {
  const passed = input.result.trim().toLowerCase() === "passed";
  const cartonHex = input.cartonInventoryBoxHex?.replace(/-/g, "").trim().toLowerCase() ?? "";

  if (!passed) {
    return {
      heading: "Carton",
      detail: "Not packed — inspection failed",
    };
  }

  if (cartonHex === "") {
    return {
      heading: "Carton",
      detail: "Not in a carton (passed but not packed, or removed from carton)",
    };
  }

  const shortCode = clampQcInventoryBoxShortCodeFromHex(cartonHex);
  const openHex = input.openCartonHex?.replace(/-/g, "").trim().toLowerCase() ?? "";

  if (openHex !== "" && cartonHex === openHex) {
    return {
      heading: "Carton",
      detail: `This open carton (${shortCode})`,
    };
  }

  return {
    heading: "Carton",
    detail: `Different carton (${shortCode}) — not the open carton on screen`,
  };
}
